import { NativeBridge } from "./native_bridge.js";
import type { CheckpointName, CrashCheckpoint } from "./types.js";

const STORAGE_KEY = 'bb_crash_checkpoint_v1';
const CLEAN_NAMES: ReadonlySet<string> = new Set<CheckpointName>( [ 'clean_exit', 'backgrounded' ] );

export class CrashCheckpoints
{
  private static current: CrashCheckpoint | null = null;
  private static previous: CrashCheckpoint | null = null;
  private static previous_consumed = false;
  private static seq = 0;
  private static session_id: string | null = null;
  private static readonly listeners: Array<( name: CheckpointName ) => void> = [];

  public static init(): void
  {
    this.previous = this.read();
    this.previous_consumed = false;
  }

  public static setSessionId( session_id: string | null ): void
  {
    this.session_id = session_id;
  }

  public static mark( name: CheckpointName ): void
  {
    if ( this.current?.name === name && name === 'in_game' && Date.now() - Date.parse( this.current.at ) < 1_000 )
      return;
    const record: CrashCheckpoint = {
      name,
      at: new Date().toISOString(),
      session_id: this.session_id,
      seq: ++this.seq,
    };
    this.current = record;
    this.write( record );
    NativeBridge.checkpoint( name, record.at );
    for ( const fn of this.listeners )
    {
      try { fn( name ); }
      catch {}
    }
  }

  public static onMark( fn: ( name: CheckpointName ) => void ): void
  {
    this.listeners.push( fn );
  }

  public static current_checkpoint(): CrashCheckpoint | null
  {
    return this.current;
  }

  public static previousRun(): CrashCheckpoint | null
  {
    return this.previous;
  }

  public static previousRunWasAbnormal(): boolean
  {
    return this.previous != null && !CLEAN_NAMES.has( this.previous.name );
  }

  public static consumePreviousRun(): CrashCheckpoint | null
  {
    if ( this.previous_consumed ) return null;
    this.previous_consumed = true;
    return this.previous;
  }

  private static read(): CrashCheckpoint | null
  {
    try
    {
      const raw = localStorage.getItem( STORAGE_KEY );
      if ( !raw ) return null;
      const parsed = JSON.parse( raw ) as Partial<CrashCheckpoint>;
      if ( typeof parsed?.name !== 'string' || typeof parsed.at !== 'string' ) return null;
      return {
        name: parsed.name as CheckpointName,
        at: parsed.at,
        session_id: typeof parsed.session_id === 'string' ? parsed.session_id : null,
        seq: typeof parsed.seq === 'number' ? parsed.seq : 0,
      };
    }
    catch
    {
      return null;
    }
  }

  private static write( record: CrashCheckpoint ): void
  {
    try { localStorage.setItem( STORAGE_KEY, JSON.stringify( record ) ); }
    catch {}
  }
}
